import { ChevronRight, TextSnippet } from "@mui/icons-material";
import { Pagination } from "@mui/material";
import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import EmptyState from "../../../../components/EmptyState";

interface Props {
  collections: any[];
  emptyIcon?: ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
}

function formatDate(value: unknown) {
  if (!value) {
    return null;
  }
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "medium" }).format(date);
}

function locationLine(forms: any) {
  return [forms?.bairro, forms?.cidade].filter(Boolean).join(" · ");
}

export default function CollectionList(props: Props) {
  const {
    collections,
    emptyIcon,
    emptyTitle = "Nenhuma avaliação encontrada",
    emptyDescription = "Ajuste os filtros ou registre uma nova avaliação.",
  } = props;

  const [page, setPage] = useState(1);
  const itemsPerPage = 6;
  const pageCount = Math.ceil(collections.length / itemsPerPage) || 1;
  const safePage = Math.min(page, pageCount);
  const indexOfLastItem = safePage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = collections.slice(indexOfFirstItem, indexOfLastItem);

  if (collections.length === 0) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        description={emptyDescription}
        actionLabel="Nova avaliação"
        actionTo="/avaliacoes/criar"
      />
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-3">
        {currentItems.map((collection, index) => {
          const forms = collection.forms ?? {};
          const date = formatDate(collection.createdAt || forms.date);
          const place = locationLine(forms);

          return (
            <Link
              key={collection.id ?? `scratch-${index}`}
              className="group relative flex w-full min-w-0 items-center gap-4 rounded-xl border border-border bg-white p-4 shadow-sm transition-colors hover:border-primary/40 hover:bg-primaryLight/40 sm:p-5"
              to={
                !collection.scratch
                  ? `/avaliacoes/${collection.id}`
                  : `/avaliacoes/criar?scratch=true`
              }
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primaryLight">
                {!collection.scratch ? (
                  <div className="h-6 w-6 text-primary" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z"
                      />
                    </svg>
                  </div>
                ) : (
                  <TextSnippet className="text-primary" aria-hidden="true" />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-lg font-semibold text-primary">
                    {forms.property || "Avaliação sem nome"}
                  </p>
                  {collection.scratch && (
                    <span className="rounded-full bg-warn/30 px-2 py-0.5 text-xs font-bold text-gray">
                      Rascunho
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-sm text-muted">
                  {[forms.client && `Pesquisador(a): ${forms.client}`, place, date]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>

              <ChevronRight
                className="hidden shrink-0 text-muted group-hover:text-primary sm:block"
                aria-hidden="true"
              />
            </Link>
          );
        })}
      </div>
      {pageCount > 1 && (
        <Pagination
          className="mx-auto !mt-8"
          count={pageCount}
          page={safePage}
          onChange={(_, value) => setPage(value)}
          variant="outlined"
          shape="rounded"
          color="primary"
        />
      )}
    </div>
  );
}
